// src/components/Sidebar.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaTimes,
  FaHome,
  FaHistory,
  FaTruck,
  FaChartBar,
  FaUsers,
  FaList,
  FaBoxOpen,
  FaSignOutAlt,
  FaChevronLeft,
  FaArrowDown,
  FaClipboardList,
  FaKey,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";

import defaultLogo from "../assets/img/logo/logosipedas.png";

export default function Sidebar({
  isOpen = false,
  onClose = () => {},
  activePage: propActivePage,

  onShowDashboard,
  onShowHistory,
  onShowPengiriman,

  onShowUsers,
  onShowCategories,
  onShowProducts,
  onShowProductIncoming,
  onShowReport,

  onShowInventoryReport,
  onShowChangePassword,

  logoSrc,
  userRole = "regular",
  onLogout = () => {},
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sb_collapsed") || "false");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem("sb_collapsed", JSON.stringify(collapsed));
  }, [collapsed]);

  const isAdmin = userRole === "admin";

  // ✅ lock body scroll saat drawer open (mobile)
  useEffect(() => {
    const isMobile = window.innerWidth <= 992;
    if (!isMobile) return;

    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev || "";
      };
    }
  }, [isOpen]);

  const activeFromRoute = useMemo(() => {
    if (pathname.startsWith("/submissions")) return "history";
    if (pathname.startsWith("/pengiriman")) return "pengiriman";
    if (pathname.startsWith("/change-password")) return "changePassword";
    if (pathname.startsWith("/regular")) return "dashboard";

    if (pathname.startsWith("/admin/users")) return "users";
    if (pathname.startsWith("/admin/categories")) return "categories";
    if (pathname.startsWith("/admin/products")) return "products";
    if (pathname.startsWith("/admin/produk-masuk")) return "productIncoming";
    if (pathname.startsWith("/admin/laporan-persediaan")) return "inventoryReport";
    if (pathname.startsWith("/admin/submissions")) return "history";
    if (pathname.startsWith("/admin/pengiriman")) return "pengiriman";
    if (pathname.startsWith("/admin/reports")) return "report";
    if (pathname.startsWith("/admin/change-password")) return "changePassword";

    if (pathname.startsWith("/admin")) return "admin";
    return "dashboard";
  }, [pathname]);

  const activePage = propActivePage ?? activeFromRoute;

  const closeIfMobile = () => {
    if (window.innerWidth <= 992) onClose?.();
  };

  const goRegular = {
    dashboard: () => {
      onShowDashboard?.();
      navigate("/regular");
      closeIfMobile();
    },
    history: () => {
      onShowHistory?.();
      navigate("/submissions"); // ✅ SPA navigation (no reload)
      closeIfMobile();
    },
    pengiriman: () => {
      onShowPengiriman?.();
      navigate("/pengiriman");
      closeIfMobile();
    },
    changePassword: () => {
      if (typeof onShowChangePassword === "function") onShowChangePassword();
      navigate("/change-password");
      closeIfMobile();
    },
  };

  const goAdminSmart = (cb, href) => {
    if (typeof cb === "function") {
      cb();
      closeIfMobile();
      return;
    }
    if (href) {
      navigate(href);
      closeIfMobile();
    }
  };

  const mainItems = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <FaHome />, onClick: goRegular.dashboard },
      { key: "history", label: "History Pengajuan", icon: <FaHistory />, onClick: goRegular.history },
      { key: "pengiriman", label: "Pengiriman", icon: <FaTruck />, onClick: goRegular.pengiriman },
      { key: "changePassword", label: "Ubah Password", icon: <FaKey />, onClick: goRegular.changePassword },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const adminItems = useMemo(
    () =>
      isAdmin
        ? [
            { key: "users", label: "Pengguna", icon: <FaUsers />, cb: onShowUsers, href: "/admin/users" },
            { key: "categories", label: "Kategori", icon: <FaList />, cb: onShowCategories, href: "/admin/categories" },
            { key: "products", label: "Produk", icon: <FaBoxOpen />, cb: onShowProducts, href: "/admin/products" },
            { key: "productIncoming", label: "Produk Masuk", icon: <FaArrowDown />, cb: onShowProductIncoming, href: "/admin/produk-masuk" },
            { key: "history", label: "Pengajuan", icon: <FaHistory />, cb: onShowHistory, href: "/admin/submissions" },
            { key: "pengiriman", label: "Pengiriman", icon: <FaTruck />, cb: onShowPengiriman, href: "/admin/pengiriman" },
            { key: "report", label: "Laporan", icon: <FaChartBar />, cb: onShowReport, href: "/admin/reports" },
            {
              key: "inventoryReport",
              label: "Laporan Persediaan",
              icon: <FaClipboardList />,
              cb: onShowInventoryReport,
              href: "/admin/laporan-persediaan",
            },
            {
              key: "changePassword",
              label: "Ubah Password",
              icon: <FaKey />,
              cb: onShowChangePassword,
              href: "/admin/change-password",
            },
          ]
        : [],
    [
      isAdmin,
      onShowUsers,
      onShowCategories,
      onShowProducts,
      onShowProductIncoming,
      onShowHistory,
      onShowPengiriman,
      onShowReport,
      onShowInventoryReport,
      onShowChangePassword,
    ]
  );

  const css = `
:root{
  --sb-w: 260px;
  --sb-w-mini: 72px;
  --sb-bg: #0ea5a5;
  --sb-border: rgba(255,255,255,.15);
  --sb-accent: #ffffff;
  --sb-text: #eaffff;
  --sb-muted: rgba(255,255,255,.7);
  --sb-hover: rgba(255,255,255,.10);
  --sb-shadow: 0 6px 24px rgba(0,0,0,.06);
}

.sb.sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 2200;
  height: 100vh;
  width: var(--sb-w);
  background: var(--sb-bg);
  border-right: 1px solid var(--sb-border);
  box-shadow: var(--sb-shadow);
  display: flex;
  flex-direction: column;
  transition: width .18s ease, transform .24s ease;
}

@media (min-width: 993px) {
  .sb.sidebar[data-collapsed="true"] { width: var(--sb-w-mini); }
}

@media (max-width: 992px) {
  .sb.sidebar {
    transform: translateX(-100%);
    width: min(84vw, 320px);
  }
  .sb.sidebar[data-open="true"] {
    transform: translateX(0);
  }
}

.sb-overlay { display: none; }
@media (max-width: 992px) {
  .sb-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,.25);
    z-index: 2100;
    opacity: 0;
    pointer-events: none;
    transition: opacity .2s ease;
  }
  .sb-overlay[data-open="true"] {
    opacity: 1;
    pointer-events: auto;
  }
}

.sb-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 14px 14px 12px 14px;
  border-bottom: 1px solid var(--sb-border);
}
.sb-brand { display: flex; align-items: center; gap: 10px; min-width: 0; }

.sb-logoBox{
  width: 40px; height: 40px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255,255,255,.15);
  display: grid;
  place-items: center;
}
.sb-logoImg{ width: 100%; height: 100%; object-fit: contain; }

.sb-title {
  font-size: 15px; font-weight: 700; color: var(--sb-accent);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.sb-close, .sb-collapse {
  border: 1px solid var(--sb-border);
  background: rgba(255,255,255,.08);
  color: var(--sb-accent);
  width: 36px; height: 36px; border-radius: 10px; cursor: pointer;
  display: grid; place-items: center;
}
@media (min-width: 993px) { .sb-close { display: none; } }
@media (min-width: 993px) { .sb-collapse { display: grid; } }
@media (max-width: 992px) { .sb-collapse { display: none; } }

.sb-scroll { flex: 1; overflow: auto; padding: 10px; }
.sb-nav { list-style: none; margin: 0; padding: 6px; }

.sb-item {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  color: var(--sb-text);
  cursor: pointer;
  transition: background .12s ease, color .12s ease;
}
.sb-item:hover { background: var(--sb-hover); }
.sb-item[data-active="true"] {
  background: rgba(255,255,255,.18);
  color: #ffffff;
  font-weight: 700;
}
.sb-ic { font-size: 16px; min-width: 16px; display: grid; place-items: center; }
.sb-tx { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 14px; }

@media (min-width: 993px) {
  .sb.sidebar[data-collapsed="true"] .sb-tx { display: none; }
}

.sb-footer {
  border-top: 1px solid var(--sb-border);
  padding: 10px;
  display: grid;
  gap: 8px;
}
.sb-role {
  display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: var(--sb-muted);
  padding: 8px 10px;
  background: rgba(255,255,255,.08);
  border: 1px solid var(--sb-border);
  border-radius: 10px;
}
.sb-logout {
  display: flex; align-items: center; gap: 10px;
  border: 1px solid var(--sb-border);
  background: rgba(255,255,255,.08);
  color: #fff;
  padding: 10px 12px; border-radius: 12px; cursor: pointer;
  font-weight: 700;
}
.sb-logout:hover { background: rgba(255,255,255,.14); }
`;

  const resolvedLogo = logoSrc || defaultLogo;

  return (
    <>
      <div className="sb-overlay" data-open={isOpen ? "true" : "false"} onClick={onClose} />
      <aside
        className="sb sidebar"
        data-open={isOpen ? "true" : "false"}
        data-collapsed={collapsed ? "true" : "false"}
      >
        <style>{css}</style>

        <div className="sb-header">
          <div className="sb-brand" title="SIPEDAS">
            <div className="sb-logoBox">
              <img src={resolvedLogo} alt="Logo SIPEDAS" className="sb-logoImg" loading="lazy" />
            </div>
            <div className="sb-title">{collapsed ? "" : "SIPEDAS"}</div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="sb-collapse"
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? "Perluas Sidebar" : "Ciutkan Sidebar"}
            >
              <FaChevronLeft
                style={{
                  transform: collapsed ? "rotate(180deg)" : "none",
                  transition: "transform .18s ease",
                }}
              />
            </button>
            <button className="sb-close" onClick={onClose} aria-label="Tutup sidebar">
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="sb-scroll">
          <ul className="sb-nav">
            {isAdmin
              ? adminItems.map((m) => (
                  <li
                    key={m.key}
                    className="sb-item"
                    data-active={activePage === m.key ? "true" : "false"}
                    onClick={() => goAdminSmart(m.cb, m.href)}
                    title={collapsed ? m.label : undefined}
                  >
                    <span className="sb-ic">{m.icon}</span>
                    <span className="sb-tx">{m.label}</span>
                  </li>
                ))
              : mainItems.map((m) => (
                  <li
                    key={m.key}
                    className="sb-item"
                    data-active={activePage === m.key ? "true" : "false"}
                    onClick={m.onClick}
                    title={collapsed ? m.label : undefined}
                  >
                    <span className="sb-ic">{m.icon}</span>
                    <span className="sb-tx">{m.label}</span>
                  </li>
                ))}
          </ul>
        </div>

        <div className="sb-footer">
          <div className="sb-role">
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                background: isAdmin ? "#0ea5e9" : "#10b981",
              }}
            />
            <span>{isAdmin ? "Admin" : "Pengguna Biasa"}</span>
          </div>
        </div>
      </aside>
    </>
  );
}
